import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useKcToken } from "../../setup/auth/useKcToken";
import {
  EOrganizationState,
  useOrganizationState,
} from "../../setup/util/useOrganizationState";
import Spinner from "../common/Spinner";
import styles from "./styles.module.scss";
import CreateOrganizationForm, {
  ISubmittedOrganization,
} from "./CreateOrganizationForm";
import OrganizationGateShell from "./OrganizationGateShell";
import OrganizationDocuments, {
  ISubmittedDocument,
} from "./OrganizationDocuments";
import OrganizationPending, { IReviewDocument } from "./OrganizationPending";
import OrganizationRejected from "./OrganizationRejected";
import { ORGANIZATION_DOCUMENTS_STEP_ENABLED } from "./organizationDocuments.constants";
import {
  EDocumentsState,
  useOrganizationDocuments,
} from "./useOrganizationDocuments";

interface Props {
  children: React.ReactNode;
}

/**
 * Gates the portal dashboard behind the logged-in account's organization state:
 * no org yet -> registration form, PENDING -> upload documents, then the under-review screen,
 * REJECTED -> rejection + resubmit, VERIFIED -> render children as-is.
 *
 * Every gated state renders inside OrganizationGateShell, which reuses the app's own sidebar
 * and topbar, so the gate looks like the portal rather than a separate app.
 */
const OrganizationGate: React.FC<Props> = ({ children }) => {
  const { t } = useTranslation();
  const token = useKcToken();
  const orgState = useOrganizationState(token);
  // Local override so the UI reflects a just-completed submission immediately,
  // without waiting on a token refresh or re-polling the fallback endpoint.
  const [justSubmitted, setJustSubmitted] =
    useState<ISubmittedOrganization | null>(null);
  const [submittedDocuments, setSubmittedDocuments] = useState<
    ISubmittedDocument[] | null
  >(null);

  const organizationCode = justSubmitted?.code ?? orgState.code;
  const isPending = orgState.state === EOrganizationState.PENDING;
  /** Registered in this session: the organization is new, so it cannot have documents yet. */
  const isFreshRegistration = !!justSubmitted && !submittedDocuments;

  // Only a returning user needs looking up — it decides whether they see their documents on the
  // review screen or get asked to upload. A fresh registration is never looked up: there is
  // nothing to find, and letting a lookup result gate the upload step is how the step ended up
  // unreachable before.
  const existing = useOrganizationDocuments(
    organizationCode,
    ORGANIZATION_DOCUMENTS_STEP_ENABLED &&
      !submittedDocuments &&
      isPending &&
      !isFreshRegistration
  );

  const renderGated = (content: React.ReactNode, statusLabel: string) => (
    <OrganizationGateShell
      organizationCode={organizationCode}
      organizationName={justSubmitted?.name ?? orgState.name}
      organizationLogoUrl={justSubmitted?.logoUrl ?? orgState.logoUrl}
      organizationStatusLabel={statusLabel}>
      {content}
    </OrganizationGateShell>
  );

  /**
   * A pending organization uploads its documents first, then sees the review screen. On a later
   * visit the attachments already on file stand in for the session state.
   */
  const renderPendingFlow = () => {
    const showPending = (documents?: IReviewDocument[]) =>
      renderGated(
        <OrganizationPending documents={documents} />,
        t("lbl_org_gate_status_pending") as string
      );

    const showDocuments = () =>
      renderGated(
        // organizationCode isn't required to render: gating on it would let a missing code
        // swallow the step. A bad code surfaces as a visible upload error instead.
        <OrganizationDocuments
          organizationCode={organizationCode ?? ""}
          onSubmitted={setSubmittedDocuments}
        />,
        t("lbl_org_gate_status_documents") as string
      );

    // Just uploaded — no need to ask the server what we already know.
    if (submittedDocuments) {
      return showPending(
        submittedDocuments.map(({ type, labelKey, fileName }) => ({
          key: type,
          title: t(labelKey),
          fileName,
        }))
      );
    }

    if (!ORGANIZATION_DOCUMENTS_STEP_ENABLED) return showPending();

    // Straight to the upload step after registering — no lookup, nothing that can hide it.
    if (isFreshRegistration) return showDocuments();

    if (existing.state === EDocumentsState.LOADING) {
      return renderGated(
        <div className={styles.container}>
          <Spinner />
        </div>,
        t("lbl_org_gate_status_pending") as string
      );
    }

    if (existing.state === EDocumentsState.PRESENT) {
      return showPending(
        existing.documents.map((doc) => ({
          key: String(doc.id),
          title: doc.title,
          // The original file name is what we store in `description` on upload.
          fileName: doc.description,
        }))
      );
    }

    // ABSENT — nothing on file, or the lookup failed. Either way, let them upload.
    return showDocuments();
  };

  if (justSubmitted) {
    return renderPendingFlow();
  }

  switch (orgState.state) {
    case EOrganizationState.LOADING:
      return (
        <div className={styles.container}>
          <Spinner />
        </div>
      );
    case EOrganizationState.NO_ORGANIZATION:
      return renderGated(
        <div className={styles.container}>
          <CreateOrganizationForm onSubmitted={setJustSubmitted} />
        </div>,
        ""
      );
    case EOrganizationState.PENDING:
      return renderPendingFlow();
    case EOrganizationState.REJECTED:
      return renderGated(
        <OrganizationRejected
          rejectionReason={orgState.rejectionReason}
          code={orgState.code}
          name={orgState.name}
          logoUrl={orgState.logoUrl}
          websiteUrl={orgState.websiteUrl}
          description={orgState.description}
          onResubmit={(organization) =>
            setJustSubmitted(
              organization ?? {
                code: orgState.code ?? "",
                name: orgState.name ?? "",
              }
            )
          }
        />,
        t("lbl_org_gate_status_rejected") as string
      );
    case EOrganizationState.VERIFIED:
    default:
      return <>{children}</>;
  }
};

export default OrganizationGate;
